import * as React from "react";
// FIXME: eslint fix かけても問題なくなったら object-curly-newline を修正する
// eslint-disable-next-line object-curly-newline
import form, { Form, FormValues, FormValidates, FormAddress } from "@/utils/form";
import logger from "@/mixins/redux-hooks-logger";
import { FetchMansionUnitsResponse } from "@/apis/property";
import PropertyType from "@/constants/PropertyType";
import { isAllBlank, isNotAllBlank } from "@/utils/is";
import ConnectingStatus from "../constants/ConnectingStatus";

// action
type UpdateFormValuesAction = {
  type: "UPDATE_FORM_VALUES";
  payload: {
    values: Partial<FormValues>;
    validates: Partial<FormValidates>;
  };
};

type UpdateUnitsAction = {
  type: "UPDATE_UNITS";
  payload: FetchMansionUnitsResponse[];
};

type UpdateConfirmMailAddressAction = {
  type: "UPDATE_CONFIRM_MAIL_ADDRESS";
  payload: { value: string };
};

type UpdateAddressIdsAction = {
  type: "UPDATE_ADDRESS_IDS";
  payload: { addressIds: Partial<FormAddress> };
};

type Action = UpdateFormValuesAction | UpdateUnitsAction | UpdateConfirmMailAddressAction | UpdateAddressIdsAction;

export const updateFormValues: (payload: {
  values: Partial<FormValues>;
  validates: Partial<FormValidates>;
}) => UpdateFormValuesAction = payload => ({
  type: "UPDATE_FORM_VALUES",
  payload,
});

export const updateUnits: (payload: FetchMansionUnitsResponse[]) => UpdateUnitsAction = payload => ({
  type: "UPDATE_UNITS",
  payload,
});

export const updateAddressIds: (payload: { addressIds: Partial<FormAddress> }) => UpdateAddressIdsAction = payload => ({
  type: "UPDATE_ADDRESS_IDS",
  payload,
});

// reducer
const reducer: React.Reducer<Form, Action> = (state: Form = form, action: Action): Form => {
  switch (action.type) {
    case "UPDATE_FORM_VALUES":
      return {
        ...state,
        values: {
          ...state.values,
          ...action.payload.values,
        },
        validates: {
          ...state.validates,
          ...action.payload.validates,
        },
      };
    case "UPDATE_UNITS":
      return {
        ...state,
        units: action.payload,
      };

    case "UPDATE_CONFIRM_MAIL_ADDRESS":
      return {
        ...state,
        values: {
          ...state.values,
          confirmMailAddress: action.payload.value,
        },
        validates: {
          ...state.validates,
          confirmMailAddress:
            state.values.mailAddress === action.payload.value ? "" : "確認用のメールアドレスが一致していません",
        },
      };
    case "UPDATE_ADDRESS_IDS":
      return {
        ...state,
        addressIds: {
          ...state.addressIds,
          ...action.payload.addressIds,
        },
      };
    default:
      return state;
  }
};

// context

export interface IFormContext {
  state: Form;
  dispatch: (action: Action) => void;
  isValidProperty: (
    isTrafficInfoAutoLinked?: boolean,
    isTotalStoryEnabled?: boolean,
    isStructureEnabled?: boolean
  ) => boolean;
  isValidPropertyDetails: (isConnectingRoadInfoAutoEnabled?: boolean) => boolean;
  isValidCustomer: () => boolean;
  isRequiredProperty: (
    isTrafficInfoAutoLinked?: boolean,
    isTotalStoryEnabled?: boolean,
    isStructureEnabled?: boolean
  ) => boolean;
  isRequiredPropertyDetails: (isConnectingRoadInfoAutoEnabled?: boolean) => boolean;
  isRequiredCustomer: (notCheck: {
    kana: boolean;
    tel: boolean;
    address: boolean;
    postCode: boolean;
    shopPrefId: boolean;
    shopId: boolean;
    referralSource: boolean;
  }) => boolean;
  isRequiredReason: (notCheck: boolean, reasonForSale: boolean) => boolean;
}

export const initFormStore: (initialState: Form) => IFormContext = initialState => {
  const [state, dispatch] = React.useReducer<React.Reducer<Form, Action>>(
    process.env.NODE_ENV === "development" ? logger(reducer) : reducer,
    initialState
  );

  const { values, validates, addressIds } = state;

  const isValidProperty: (
    isTrafficInfoAutoLinked?: boolean,
    isTotalStoryEnabled?: boolean,
    isStructureEnabled?: boolean
  ) => boolean = (isTrafficInfoAutoLinked?: boolean, isTotalStoryEnabled?: boolean, isStructureEnabled?: boolean) => {
    switch (state.values.propertyType) {
      case PropertyType.MANSION: {
        let defaultParams = [
          validates.propertyName,
          validates.propertyPref,
          validates.propertyCity,
          validates.propertyTown,
          validates.builtDateYear,
          validates.builtDateMonth,
          ...(isTrafficInfoAutoLinked
            ? [validates.propertyStreet]
            : [validates.line, validates.station, validates.walk]),
          ...(isTotalStoryEnabled ? [validates.totalStory] : []),
          ...(isStructureEnabled ? [validates.structure] : []),
        ];

        return isAllBlank(...defaultParams);
      }
      case PropertyType.HOUSE:
      case PropertyType.LAND: {
        let defaultParams = [
          validates.propertyPref,
          validates.propertyCity,
          validates.propertyTown,
          validates.propertyStreet,
          validates.builtDateYear,
          validates.builtDateMonth,
        ];
        if (isTrafficInfoAutoLinked) {
          defaultParams = [...defaultParams, validates.propertyStreet];
        } else {
          defaultParams = [...defaultParams, validates.line, validates.station, validates.bus, validates.walk];
        }
        return isAllBlank(...defaultParams);
      }
      default:
        throw new Error("type is invalid.");
    }
  };

  const isValidPropertyDetails: (isConnectingRoadInfoAutoEnabled?: boolean) => boolean = (
    isConnectingRoadInfoAutoEnabled?: boolean
  ) => {
    switch (values.propertyType) {
      case PropertyType.MANSION:
        return isAllBlank(
          validates.roomNumber,
          validates.unitSize,
          validates.story,
          validates.balconyDirection,
          validates.floorPlan
        );
      case PropertyType.HOUSE: {
        let defaultParams: Array<string | undefined> = [
          validates.landSize,
          validates.houseSize,
          validates.structure,
          validates.totalStory,
          validates.undergroundStory,
          validates.floorPlan,
        ];
        if (isConnectingRoadInfoAutoEnabled === true) {
          defaultParams = [...defaultParams, validates.connecting];
        }
        return isAllBlank(...defaultParams);
      }
      case PropertyType.LAND: {
        let defaultParams: Array<string | undefined> = [validates.landSize];
        if (!isConnectingRoadInfoAutoEnabled === true) {
          defaultParams = [...defaultParams, validates.connecting];
        }
        return isAllBlank(...defaultParams);
      }
      default:
        throw new Error("type is invalid.");
    }
  };

  const isValidCustomer = () => {
    return isAllBlank(
      validates.lastName,
      validates.firstName,
      validates.lastNameKana,
      validates.firstNameKana,
      validates.tel,
      validates.mailAddress,
      validates.confirmMailAddress,
      validates.postCode,
      validates.pref,
      validates.city,
      validates.town,
      validates.street,
      validates.building
    );
  };

  const isRequiredProperty: (
    isTrafficInfoAutoLinked?: boolean,
    isTotalStoryEnabled?: boolean,
    isStructureEnabled?: boolean
  ) => boolean = (isTrafficInfoAutoLinked?: boolean, isTotalStoryEnabled?: boolean, isStructureEnabled?: boolean) => {
    switch (values.propertyType) {
      case PropertyType.MANSION: {
        let defaultParams = [
          values.propertyName,
          values.propertyPref,
          values.propertyCity,
          values.propertyTown,
          values.builtDateYear,
          values.builtDateMonth,
          addressIds.prefId,
          addressIds.cityId,
          addressIds.townId,
          ...(isTrafficInfoAutoLinked ? [values.propertyStreet] : [values.line, values.station, values.walk]),
          ...(isTotalStoryEnabled ? [values.totalStory] : []),
          ...(isStructureEnabled ? [values.structure] : []),
        ];

        return isNotAllBlank(...defaultParams);
      }
      case PropertyType.HOUSE:
      case PropertyType.LAND: {
        let defaultParams = [
          values.propertyPref,
          values.propertyCity,
          values.propertyTown,
          values.propertyStreet,
          values.builtDateYear,
          values.builtDateMonth,
          addressIds.prefId,
          addressIds.cityId,
          addressIds.townId,
        ];
        if (isTrafficInfoAutoLinked) {
          defaultParams = [...defaultParams, values.propertyStreet];
        } else {
          defaultParams = [...defaultParams, values.line, values.station, values.walk];
        }
        return isNotAllBlank(...defaultParams);
      }
      default:
        throw new Error("type is invalid.");
    }
  };

  const isRequiredPropertyDetails: (isConnectingRoadInfoAutoEnabled?: boolean) => boolean = (
    isConnectingRoadInfoAutoEnabled?: boolean
  ) => {
    const connecting =
      values.connecting.status === ConnectingStatus.NO_SIDE
        ? true
        : isNotAllBlank(...values.connecting.details.reduce((ret, d) => ret.concat(Object.values(d)), [] as string[]));
    switch (values.propertyType) {
      case PropertyType.MANSION:
        return isNotAllBlank(
          values.roomNumber,
          values.unitSize,
          values.floorPlan,
          values.balconyDirection,
          values.story
        );
      case PropertyType.HOUSE: {
        const isPropertyDetailsNotAllBlank = isNotAllBlank(
          values.totalStory,
          values.houseSize,
          values.landSize,
          values.structure,
          values.floorPlan
        );
        if (isConnectingRoadInfoAutoEnabled === true) {
          return isPropertyDetailsNotAllBlank;
        } else {
          return (
            isNotAllBlank(
              values.totalStory,
              values.houseSize,
              values.landSize,
              values.structure,
              values.connecting.status,
              values.floorPlan
            ) && connecting
          );
        }
      }
      case PropertyType.LAND:
        const isPropertyDetailsNotAllBlank = isNotAllBlank(values.landSize);
        if (isConnectingRoadInfoAutoEnabled === true) {
          return isPropertyDetailsNotAllBlank;
        } else {
          return isNotAllBlank(values.landSize, values.connecting.status) && connecting;
        }
      default:
        throw new Error("type is invalid.");
    }
  };

  const isRequiredCustomer = (notCheck: {
    kana: boolean;
    tel: boolean;
    address: boolean;
    postCode: boolean;
    shopPrefId: boolean;
    shopId: boolean;
    referralSource: boolean;
  }) => {
    const checkValues = [values.lastName, values.firstName, values.mailAddress, values.confirmMailAddress];
    if (!notCheck.kana) {
      checkValues.push(values.lastNameKana, values.firstNameKana);
    }
    if (!notCheck.tel) {
      checkValues.push(values.tel);
    }
    if (!notCheck.address) {
      if (!notCheck.postCode) {
        checkValues.push(values.postCode);
      }
      checkValues.push(values.pref, values.city, values.town, values.street);
    }

    if (!notCheck.shopPrefId) {
      checkValues.push(values.shopPrefId);
    }
    if (!notCheck.shopId) {
      checkValues.push(values.shopId);
    }
    if (!notCheck.referralSource) {
      checkValues.push(values.referralSourceCd);
    }
    return isNotAllBlank(...checkValues);
  };

  const isRequiredReason = (notCheck: boolean, reasonForSale: boolean): boolean => {
    if (notCheck && notCheck === true) {
      return notCheck;
    }

    if ((reasonForSale === null || reasonForSale === undefined) && notCheck === false) {
      return isNotAllBlank(values.salePeriod, values.relation);
    }

    if (notCheck === false && reasonForSale === true) {
      return isNotAllBlank(values.saleReason, values.salePeriod, values.relation);
    }

    if (notCheck === false && reasonForSale === false) {
      return isNotAllBlank(values.salePeriod, values.relation);
    }

    return false;
  };

  return {
    state,
    dispatch: action => {
      dispatch(action);
    },
    isValidProperty,
    isValidPropertyDetails,
    isValidCustomer,
    isRequiredProperty,
    isRequiredPropertyDetails,
    isRequiredCustomer,
    isRequiredReason,
  };
};

export default React.createContext<IFormContext | null>(null);
